import { createApi } from '@reduxjs/toolkit/query/react'
import { generateRESTQuery, getBaseQuery } from './apiUtils'
import apiConstants, { HTTP_METHODS } from './constants'

interface NotificationsListPayload {
  items: TenantNotificationItem[]
  pagination: {
    totalItems: number
    pageSize: number
    itemsIndex?: {
      start: number
      end: number
    }
  }
}

type CreateNotificationResult =
  | string
  | number
  | {
      NotificationID?: string | number
      notificationID?: string | number
    }

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  tagTypes: ['AllNotifications'],
  endpoints: (builder) => ({
    getNotificationsByTenantID: builder.query<
      NotificationsListPayload,
      GetNotificationsParams
    >({
      query: (requestParams: GetNotificationsParams) =>
        generateRESTQuery({
          path: apiConstants.REST_NOTIFICATIONS_PATH,
          method: HTTP_METHODS.GET,
          queryParams: {
            tenantID: requestParams.TenantID,
            pageNumber: requestParams.pageNumber,
            pageSize: requestParams.pageSize,
            searchText: requestParams.searchText,
            sendDateFrom: requestParams.sendDateFrom,
            sendDateTo: requestParams.sendDateTo,
            selectedStatuses: requestParams.selectedStatuses,
            selectedTypes: requestParams.selectedTypes
          }
        }),
      providesTags: [{ type: 'AllNotifications' }]
    }),
    validateNotificationSteps: builder.mutation<
      CommonRestValidationResponse,
      NotificationValidationParams
    >({
      query: (requestParams: NotificationValidationParams) =>
        generateRESTQuery({
          path: `${apiConstants.REST_NOTIFICATIONS_PATH}/validate`,
          method: HTTP_METHODS.POST,
          body: requestParams
        })
    }),
    createNotification: builder.mutation<
      { result: CreateNotificationResult },
      CreateNotificationParams
    >({
      query: (requestParams: CreateNotificationParams) =>
        generateRESTQuery({
          path: apiConstants.REST_NOTIFICATIONS_PATH,
          method: HTTP_METHODS.POST,
          body: requestParams
        }),
      invalidatesTags: [{ type: 'AllNotifications' }]
    }),
    sendTestSMSNotification: builder.mutation<
      CommonRestValidationResponse,
      SendTestSMSNotificationParams
    >({
      query: (requestParams: SendTestSMSNotificationParams) =>
        generateRESTQuery({
          path: `${apiConstants.REST_NOTIFICATIONS_PATH}/send-test-sms`,
          method: HTTP_METHODS.POST,
          body: requestParams
        })
    }),
    getSegmentsWithNotificationOptIn: builder.query<
      { result: TargetGroup[] },
      { TenantID: string }
    >({
      query: (requestParams: { TenantID: string }) =>
        generateRESTQuery({
          path: `${apiConstants.REST_NOTIFICATIONS_PATH}/segments`,
          method: HTTP_METHODS.GET,
          queryParams: {
            tenantID: requestParams.TenantID
          }
        })
    })
  })
})

export const {
  useGetNotificationsByTenantIDQuery,
  useValidateNotificationStepsMutation,
  useCreateNotificationMutation,
  useSendTestSMSNotificationMutation,
  useGetSegmentsWithNotificationOptInQuery
} = notificationsApi
